/**
 * Client-side card tokenization for BCS billing (QuickBooks Payments) —
 * Apply port of volt-portal's lib/payments/tokenizeCard.ts. Keep the two in
 * sync if the provider contract changes.
 *
 * Card data goes BROWSER → provider directly — it must never touch a Tento
 * server (that is the whole PCI posture: our backend only ever receives the
 * resulting one-time token, which is single-use with a 15-minute lifespan).
 * The endpoint + public app id come from /v1/bcm/subscription/billing-config.
 */

export interface CardDetails {
  name: string;
  number: string;
  expMonth: string;
  /** Four-digit year, per the provider contract. */
  expYear: string;
  cvc: string;
  postalCode: string;
}

export interface TokenizationConfig {
  tokenizeUrl: string;
  /** Public OAuth client id — used as the browser call's API-key header. */
  clientId: string;
}

export class CardTokenizationError extends Error {
  constructor(message: string, public readonly status: number | null) {
    super(message);
    this.name = 'CardTokenizationError';
  }
}

/** UUID for the provider Request-Id — with a fallback for older browsers
 * and non-secure contexts where crypto.randomUUID is unavailable. */
const requestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tok-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const buildBody = (card: CardDetails) => ({
  card: {
    name: card.name,
    number: card.number.replace(/\s+/g, ''),
    expMonth: card.expMonth.padStart(2, '0'),
    expYear: card.expYear,
    cvc: card.cvc,
    address: {
      postalCode: card.postalCode,
      country: 'US',
    },
  },
});

/**
 * Exchanges raw card details for a one-time provider token. Tries the plain
 * documented call first; on an auth challenge retries once with Intuit's
 * public API-key header scheme (the client id — never a secret).
 */
export async function tokenizeCard(card: CardDetails, config: TokenizationConfig): Promise<string> {
  const body = JSON.stringify(buildBody(card));

  const attempt = (headers: Record<string, string>) =>
    fetch(config.tokenizeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Request-Id': requestId(),
        ...headers,
      },
      body,
    });

  let response: Response;
  try {
    response = await attempt({});
    if ((response.status === 401 || response.status === 403) && config.clientId) {
      response = await attempt({
        Authorization: `Intuit_APIKey intuit_apikey=${config.clientId}, intuit_apikey_version=1.0`,
      });
    }
  } catch {
    throw new CardTokenizationError(
      "Couldn't reach the payment provider. Please check your connection and try again.",
      null
    );
  }

  if (!response.ok) {
    throw new CardTokenizationError(
      response.status === 400
        ? 'The card details look invalid. Please check the number, expiry and CVC.'
        : "Card couldn't be verified. Please try again.",
      response.status
    );
  }

  const parsed = (await response.json().catch(() => null)) as { value?: string } | null;
  if (!parsed?.value) {
    throw new CardTokenizationError("Card couldn't be verified. Please try again.", response.status);
  }
  return parsed.value;
}
